import { useRef } from "react";
import { motion } from "framer-motion";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";

export function GameCard({ g, i = 0, onPlay, testId }) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03 }}
      data-testid={testId || `game-card-${g.id}`} onClick={() => onPlay(g)}
      className="group relative w-full overflow-hidden rounded-xl border border-white/5 bg-[#151A22] text-left hover:-translate-y-1 hover:border-[#FFB800]/40 transition-[transform,border-color]"
    >
      <img src={g.image} alt={g.name} loading="lazy" className="w-full aspect-[180/236] object-cover group-hover:scale-105 transition-transform duration-500" />
      {g.badge && <span className={`absolute top-1.5 left-1.5 z-10 px-2 py-0.5 text-[9px] font-black text-white rounded ${g.badge === "HOT" ? "bg-gradient-to-r from-[#FF7A18] to-[#AF002D]" : "bg-[#1f8fff]"}`}>{g.badge}</span>}
      {g.category === "live" && <span className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black text-white rounded bg-black/60"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" />LIVE</span>}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05060a]/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="rounded-full bg-[#FFB800] p-3 text-[#0B0E14] shadow-[0_0_30px_rgba(255,184,0,0.5)]"><Play fill="currentColor" size={18} /></span>
        <span className="px-2 text-center text-[11px] font-bold text-white leading-tight">{g.name}</span>
        <span className="text-[9px] text-gray-300">RTP {g.rtp}%</span>
      </div>
    </motion.button>
  );
}

export function GameRow({ id, title, icon: I, games, onPlay, onSeeAll }) {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  if (!games.length) return null;
  return (
    <section className="mt-8" data-testid={`section-${id}`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-display text-lg sm:text-xl font-black uppercase"><I size={20} className="gc-text" />{title}</h2>
        <div className="flex items-center gap-1.5">
          {onSeeAll && <button data-testid={`see-all-${id}`} onClick={onSeeAll} className="rounded-lg bg-[#1F2633] px-3 py-1.5 text-[11px] font-black uppercase text-gray-300 hover:text-white transition-colors">See all {games.length}</button>}
          <button data-testid={`row-prev-${id}`} onClick={() => scroll(-1)} className="hidden sm:flex rounded-lg bg-[#1F2633] p-1.5 text-gray-300 hover:text-white transition-colors"><ChevronLeft size={16} /></button>
          <button data-testid={`row-next-${id}`} onClick={() => scroll(1)} className="hidden sm:flex rounded-lg bg-[#1F2633] p-1.5 text-gray-300 hover:text-white transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>
      <div ref={ref} className="mt-3 flex gap-3 overflow-x-auto no-scrollbar snap-x scroll-smooth">
        {games.map((g, i) => (
          <div key={g.id} className="snap-start shrink-0 w-[30%] sm:w-[22%] md:w-[18%] lg:w-[14.5%]">
            <GameCard g={g} i={i} onPlay={onPlay} testId={`game-card-${id}-${g.id}`} />
          </div>
        ))}
      </div>
    </section>
  );
}
